import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["dataType", "filterProperty", "onChange", "report", "resolvers", "schema", "value"];
import { fromJS, List } from 'immutable';
import { useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIButton from 'UIComponents/button/UIButton';
import UISelect from 'UIComponents/input/UISelect';
// @ts-expect-error Untyped dependency
import { TEAM } from 'reference-resolvers/constants/ReferenceObjectTypes';
// @ts-expect-error Untyped dependency
import ReferenceInputEnum from 'customer-data-reference-ui-components/ReferenceInputEnum';
import ReferenceLiteSearchSelect from 'reference-resolvers-lite/components/ReferenceLiteSearchSelect';
import { getPropertyResolver } from 'reference-resolvers-lite/utils/getPropertyResolver';
import { IN } from 'reporting-data/constants/operators';
import { getCustomWidgetType } from 'reporting-data/report/reportGetters';
import TeamSelect from './TeamSelect';
import SalesQuotaPipelineSelect from './SalesQuotaPipelineSelect';
import { getObjectTypeId, getReferenceType, getSpecialOptionsForReferenceType, isSalesQuotaPipelineFilter, useObjectCoordinateSafeValues } from './quickFilterEditorUtils';
import { jsx as _jsx } from "react/jsx-runtime";
const ClearButton = ({
  onClick
}) => /*#__PURE__*/_jsx(UIButton, {
  use: "link",
  onClick: onClick,
  children: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "settingLabels.quickFilter.clear"
  })
});
const QuickFilterInput = _ref => {
  let {
      dataType,
      filterProperty,
      onChange,
      report,
      resolvers,
      schema,
      value
    } = _ref,
    passThroughProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const isMultiSelect = schema.operator === IN;
  const isClearable = isMultiSelect || schema.isClearable;
  const referenceType = useMemo(() => getReferenceType(dataType, filterProperty), [dataType, filterProperty]);
  const {
    handleChange,
    value: ocSafeValue
  } = useObjectCoordinateSafeValues(filterProperty, value, onChange, isMultiSelect);
  const sharedProps = Object.assign({
    clearable: isClearable,
    ['data-test-id']: `${schema.property}-report-quick-filter`,
    // For testing purposes
    ['data-multi']: isMultiSelect,
    searchable: true,
    multi: isMultiSelect,
    multiCollapseLimit: 5,
    placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "settingLabels.quickFilter.placeholder"
    }),
    dropdownFooter: isClearable ? /*#__PURE__*/_jsx(ClearButton, {
      onClick: () => {
        onChange(isMultiSelect ? [] : null);
      }
    }) : null
  }, passThroughProps);
  const options = useMemo(() => {
    const propertyOptions = (filterProperty.options || []).map(option => ({
      text: option.label,
      value: option.value
    }));
    const specialOptions = getSpecialOptionsForReferenceType(referenceType);
    return specialOptions.concat(propertyOptions);
  }, [filterProperty, referenceType]);
  if (referenceType === TEAM) {
    return /*#__PURE__*/_jsx(TeamSelect, Object.assign({
      onChange: onChange,
      value: value
    }, sharedProps));
  }
  const objectTypeId = getObjectTypeId(dataType, filterProperty);
  const externalOptionsMetaData = filterProperty.externalOptionsMetaData;
  const externalOptionsResolver = getPropertyResolver({
    property: externalOptionsMetaData ? {
      property: filterProperty,
      externalOptionsMetaData,
      objectTypeId
    } : filterProperty,
    objectTypeId
  });
  if (externalOptionsResolver && isSalesQuotaPipelineFilter(schema.property, getCustomWidgetType(report))) {
    return /*#__PURE__*/_jsx(SalesQuotaPipelineSelect, Object.assign({
      onChange: onChange,
      report: report,
      resolver: externalOptionsResolver,
      value: value
    }, sharedProps));
  }
  if (externalOptionsResolver) {
    return /*#__PURE__*/_jsx(ReferenceLiteSearchSelect, Object.assign({}, sharedProps, {
      options: options,
      onChange: ({
        target: {
          value: newValue
        }
      }) => {
        handleChange(newValue);
      },
      resolver: externalOptionsResolver,
      value: ocSafeValue
    }));
  }
  if (referenceType && resolvers[referenceType]) {
    return /*#__PURE__*/_jsx(ReferenceInputEnum, Object.assign({}, sharedProps, {
      onChange: ({
        target: {
          value: newValue
        }
      }) => {
        const jsValue = List.isList(newValue) ? newValue.toJS() : newValue;
        onChange(jsValue);
      },
      options: options,
      resolver: resolvers[referenceType],
      value: fromJS(value)
    }));
  }
  return /*#__PURE__*/_jsx(UISelect, Object.assign({}, sharedProps, {
    anchorType: "button",
    onChange: ({
      target: {
        value: newValue
      }
    }) => {
      onChange(newValue);
    },
    options: options,
    value: value
  }));
};
export default QuickFilterInput;